/**
 * weightDeclaration.ts — "what was this patient actually asked for about their
 * weight, and what did they attest?", answered once for the review surface.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: weight self-certification
 * (clinic #166, 2026-09-04).
 *
 * ⚠️ WHY THIS EXISTS AT ALL. Until question set v3, q14 asked EVERY patient for
 * a photo of themselves on the scales, and a patient with no photo on file was
 * therefore a patient who had not done something they were asked to do — which
 * is why the review's photo card shouts about it. Requiring that image upfront
 * took throughput from ~25 leads a week to ~2, so from v3 q14 is a DECLARATION
 * the patient ticks and no photo is asked for at sign-up at all.
 *
 * That inverts the meaning of an empty gallery. For a self-certified patient,
 * NO PHOTO IS THE NORMAL CASE. A loud "Awaiting patient photo" band on their
 * review would accuse them of ignoring a request that was never made — and, worse
 * for the clinician reading it, would imply an outstanding action where there is
 * none. But absence must still be SHOWN, never implied by silence: the old
 * render-nothing is what let the one decision that needs the photo be made on a
 * screen that never admitted it was missing.
 *
 * So the card needs to know WHICH PATIENT IT IS LOOKING AT, and both kinds are
 * live at once: v1/v2 rows belong to people who are mid-treatment and WERE
 * genuinely asked. This module is that discrimination, in one place, pure and
 * unit-tested, so the photo card and the questionnaire summary can never
 * disagree about it.
 *
 * ⚠️ IT FAILS TOWARD "ASKED". Getting it wrong in that direction leaves a
 * clinician looking at a slightly over-loud band about a photo nobody needs.
 * Getting it wrong the other way SOFTENS a real failure — a patient who deferred
 * a photo and never sent it, described as having self-certified. The first is a
 * blemish; the second is the review losing information it was built to carry.
 */
import type { AssessmentReview, ReviewAnswer, ReviewSection } from '../api/types';

/**
 * The q14 deferral marker as it appears in `review.flags`. LEGACY-ONLY from set
 * v3 — nothing raises it any more (clinic-service retired it at source) — and
 * it stays because the rows that carry it belong to patients in treatment now.
 *
 * Named here rather than shared with `journey/clinicalFlag.ts`'s constant on
 * purpose: that one is subtracted from a CLINICAL signal on the patient's own
 * lane, this one is read as EVIDENCE OF THE ASK on a clinician's. Same string,
 * two different questions; folding them together would make a change to either
 * question silently answer the other.
 */
const PHOTO_DEFERRED_FLAG = 'photo_pending';

/**
 * Which weight question this patient was shown.
 *
 *  - `photo_asked`    — they were asked for an image (set v1/v2). Absence is a
 *                       failure to be shown loudly.
 *  - `self_certified` — they ticked the declaration (set v3+). Absence is the
 *                       NORMAL case, to be stated plainly.
 *  - `unknown`        — the review carries no weight question we recognise.
 *                       Claim nothing about the ask; still state the absence.
 */
export type WeightAskState = 'photo_asked' | 'self_certified' | 'unknown';

/** The `verification` section, where clinic-service's review.ts puts q14 + q15. */
function verificationItems(sections: readonly ReviewSection[]): readonly ReviewAnswer[] {
  return sections.find((section) => section.key === 'verification')?.items ?? [];
}

/**
 * Is this the q15 CONSENT-DECLARATIONS bundle rather than the weight question?
 * `verification` holds both, so every rule here has to exclude q15 explicitly.
 * The server pre-joins q15's ticked labels with "; " (no other renderer uses
 * that token — multi-selects join with ", "), which is the fallback for a
 * question set that renames the key.
 */
function isConsentBundle(item: ReviewAnswer): boolean {
  return item.questionKey === 'q15' || item.answer.includes('; ');
}

/**
 * The weight question's review item, or null.
 *
 * `q14` is the primary key, mirroring every other detector on this surface. The
 * fallback is deliberately NOT "mentions a photo": that would miss the
 * declaration entirely, which is the shape this module exists for. It is
 * "the verification item that is not the consent bundle" — under both question
 * sets `verification` contains exactly q14 and q15.
 */
export function weightQuestionItem(review: AssessmentReview): ReviewAnswer | null {
  const items = verificationItems(review.sections ?? []);
  return items.find((item) => item.questionKey === 'q14') ?? items.find((item) => !isConsentBundle(item)) ?? null;
}

/**
 * ⚠️ THE DISCRIMINATION, and the reason it reads TWO independent pieces of
 * evidence rather than one.
 *
 * 1. THE FLAG WINS. `photo_pending` is on the row only if the patient was shown
 *    the deferral option and chose it, so it is proof of the ask that survives
 *    any rewording of the question. It is checked first because it is the case
 *    where absence genuinely IS an outstanding patient action.
 * 2. THEN THE QUESTION THEY WERE SHOWN. The review projects the prompt from
 *    THEIR OWN question set (each `Assessment` keeps its `questionSetId`), so
 *    the wording on the row is the wording that patient actually read. A prompt
 *    that mentions a photo is the v1/v2 ask; anything else in that slot is the
 *    declaration.
 *
 * The `kind === 'consent' && options.length === 1` half of the estate's
 * detection rule is NOT usable here and that is a property of the wire, not a
 * shortcut: the review projection is `{questionKey, prompt, answer, detail}` —
 * clinic-service's review.ts renders every answer to prose server-side and
 * carries neither `kind` nor `options`. The prompt is the only evidence of the
 * ask this shape has, so the prompt is what is read.
 */
export function weightAskState(review: AssessmentReview): WeightAskState {
  if ((review.flags ?? []).includes(PHOTO_DEFERRED_FLAG)) return 'photo_asked';
  const item = weightQuestionItem(review);
  if (!item) return 'unknown';
  if (/photo/i.test(item.prompt)) return 'photo_asked';
  return 'self_certified';
}

/**
 * What the patient attested about their weight, ready to render — or null when
 * they were not asked to attest anything (a v1/v2 photo patient, or a review
 * with no weight question on it).
 *
 * ⚠️ EVERY FIELD IS THE SERVER'S OWN STRING, UNPARSED WHERE IT MATTERS. This is
 * the clinical record of an undertaking the patient gave, pinned
 * character-for-character in clinic-service's seed spec, and a portal that
 * paraphrased it would be putting words in a patient's mouth on the screen a
 * prescriber signs against.
 */
export interface WeightDeclarationView {
  /** The readback line the patient was shown ("My current weight is"). */
  prompt: string;
  /** The ONE tick, verbatim — the undertaking itself. */
  attested: string;
  /**
   * The weight they certified, as the capture answer renders it.
   *
   * ⚠️ IT COMES FROM q0/q1, NOT FROM q14, AND THAT IS A CLINICAL DECISION
   * RATHER THAN A CONVENIENCE. clinic-service's declaration block records
   * `weightReadback: true`: the number in the readback line is the CAPTURED
   * weight — the number the BMI hard-stop gate ran on and the number a
   * prescriber signs against. A second, separately-typed weight on q14 would
   * let the certified number and the assessed number disagree. So reading it
   * off the capture item is not an approximation of the declared weight; by the
   * server's construction it IS the declared weight.
   *
   * `null` when no capture item is on the review (an early hard stop, say) —
   * rendered as an absence, never as a guess.
   */
  declaredWeight: string | null;
}

/**
 * The capture answer, which clinic-service renders as "<h> cm · <w> kg" (its
 * review.ts `case 'capture'`).
 *
 * The kg half is pulled out because the height is noise on a weight
 * declaration — but the WHOLE string is the fallback when the format does not
 * match, rather than nothing: showing the server's own rendering is always
 * honest, and inventing a shape it did not send is not.
 */
function declaredWeightFrom(sections: readonly ReviewSection[]): string | null {
  for (const section of sections) {
    for (const item of section.items) {
      const match = /(\d+(?:\.\d+)?)\s*kg/i.exec(item.answer);
      if (match) return `${match[1]} kg`;
      if (/\bcm\b/i.test(item.answer) && /·/.test(item.answer)) return item.answer;
    }
  }
  return null;
}

export function weightDeclarationView(review: AssessmentReview): WeightDeclarationView | null {
  if (weightAskState(review) !== 'self_certified') return null;
  const item = weightQuestionItem(review);
  if (!item) return null;
  return {
    prompt: item.prompt,
    attested: item.answer,
    declaredWeight: declaredWeightFrom(review.sections ?? []),
  };
}
